import React, { useCallback, useEffect, useRef } from 'react';
import {
  MapContainer, TileLayer, Polyline, Polygon, Marker, CircleMarker,
  useMapEvents, useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Default map centre (Central Canada)
const CANADA_CENTER = [53.5, -98.0];
const DEFAULT_ZOOM  = 4;

// ── Fit map to a GeoJSON geometry bounding box ────────────────────────────────
function FitToGeometry({ geometry }) {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return;
    try {
      const layer = L.geoJSON(geometry);
      const bounds = layer.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
    } catch (_) { /* ignore */ }
  }, [geometry, map]);
  return null;
}

// ── Click capture for polygon drawing ────────────────────────────────────────
function DrawCapture({ onMapClick, onDblClick, active }) {
  const map = useMapEvents({
    click(e) {
      if (!active) return;
      // Ignore double-click's synthetic single-click
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
    dblclick(e) {
      if (!active) return;
      e.originalEvent.preventDefault();
      map.doubleClickZoom.disable();
      onDblClick();
      // Re-enable after a tick
      setTimeout(() => map.doubleClickZoom.enable(), 300);
    },
  });
  return null;
}

/**
 * FootprintPickerMap
 *
 * Props:
 *   mode:              'draw' | 'view'
 *   vertices:          Array<[lat, lng]>  — controlled from parent in draw mode
 *   onVerticesChange:  (vertices) => void — called on each new vertex / clear
 *   uploadedGeometry:  GeoJSON geometry object — shown in view or upload-preview mode
 *   readOnly:          bool — if true, no drawing allowed (just display)
 *   height:            CSS string (default '340px')
 */
export default function FootprintPickerMap({
  mode = 'draw',
  vertices = [],
  onVerticesChange,
  uploadedGeometry,
  readOnly = false,
  height = '340px',
}) {
  const drawActive = mode === 'draw' && !readOnly;

  // Convert vertices [lat,lng] array to leaflet LatLng format
  const positions = vertices.map(([lat, lng]) => [lat, lng]);

  const handleMapClick = useCallback((lat, lng) => {
    if (!onVerticesChange) return;
    onVerticesChange([...vertices, [lat, lng]]);
  }, [vertices, onVerticesChange]);

  const handleDblClick = useCallback(() => {
    // Double-click closes the polygon (doesn't add another point)
    // Vertices already contain what was clicked — just signal "done" by
    // ensuring the polygon has ≥ 3 points; no-op otherwise.
  }, []);

  const handleClear = () => { if (onVerticesChange) onVerticesChange([]); };
  const handleUndo  = () => {
    if (vertices.length > 0 && onVerticesChange) {
      onVerticesChange(vertices.slice(0, -1));
    }
  };

  // Determine geometry to fit — prefer uploadedGeometry, then drawn vertices
  const fitGeometry = uploadedGeometry
    ? uploadedGeometry
    : vertices.length >= 2
      ? { type: 'LineString', coordinates: vertices.map(([lat, lng]) => [lng, lat]) }
      : null;

  return (
    <div style={{ position: 'relative' }}>
      {drawActive && (
        <div style={{
          display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            Click to add vertices · Double-click to close polygon
          </span>
          {vertices.length > 0 && (
            <span style={{
              background: '#1e3a5f', border: '1px solid #3b82f6',
              borderRadius: 5, padding: '2px 8px', fontSize: 11, color: '#7dd3fc',
            }}>
              {vertices.length} {vertices.length === 1 ? 'vertex' : 'vertices'}
              {vertices.length >= 3 ? ' — polygon ready' : ''}
            </span>
          )}
          {vertices.length > 0 && (
            <>
              <button type="button" onClick={handleUndo} style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 5,
                color: '#94a3b8', padding: '3px 9px', cursor: 'pointer', fontSize: 11,
              }}>↩ Undo</button>
              <button type="button" onClick={handleClear} style={{
                background: '#0f172a', border: '1px solid #ef4444', borderRadius: 5,
                color: '#f87171', padding: '3px 9px', cursor: 'pointer', fontSize: 11,
              }}>✕ Clear</button>
            </>
          )}
        </div>
      )}

      <div style={{
        height, borderRadius: 8, overflow: 'hidden',
        border: '1px solid #334155',
        cursor: drawActive ? 'crosshair' : 'default',
      }}>
        <MapContainer
          center={CANADA_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          doubleClickZoom={!drawActive}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          {fitGeometry && <FitToGeometry geometry={fitGeometry} />}

          {drawActive && (
            <DrawCapture
              active={drawActive}
              onMapClick={handleMapClick}
              onDblClick={handleDblClick}
            />
          )}

          {/* Drawn vertices: show as polyline + vertex dots */}
          {positions.length >= 2 && !uploadedGeometry && (
            positions.length >= 3
              ? <Polygon
                  positions={positions}
                  pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.15, weight: 2 }}
                />
              : <Polyline
                  positions={positions}
                  pathOptions={{ color: '#3b82f6', weight: 2 }}
                />
          )}

          {/* Vertex markers */}
          {!uploadedGeometry && positions.map(([lat, lng], i) => (
            <CircleMarker
              key={i}
              center={[lat, lng]}
              radius={5}
              pathOptions={{ color: '#3b82f6', fillColor: '#1d4ed8', fillOpacity: 1, weight: 2 }}
            />
          ))}

          {/* Uploaded / read-only geometry from GeoJSON */}
          {uploadedGeometry && (() => {
            try {
              const coords = uploadedGeometry.coordinates;
              if (uploadedGeometry.type === 'Polygon' && coords?.[0]) {
                const ring = coords[0].map(([lng, lat]) => [lat, lng]);
                return (
                  <Polygon
                    positions={ring}
                    pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.15, weight: 2 }}
                  />
                );
              }
              if (uploadedGeometry.type === 'MultiPolygon' && coords) {
                return coords.map((poly, pi) => {
                  const ring = poly[0].map(([lng, lat]) => [lat, lng]);
                  return (
                    <Polygon
                      key={pi}
                      positions={ring}
                      pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.15, weight: 2 }}
                    />
                  );
                });
              }
              if (uploadedGeometry.type === 'Point' && coords) {
                return <Marker position={[coords[1], coords[0]]} />;
              }
            } catch (_) {}
            return null;
          })()}
        </MapContainer>
      </div>
    </div>
  );
}
